'use client'

import Link from 'next/link'

import { useState, useEffect, Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Loader2, Eye, EyeOff } from 'lucide-react'
import { JwaWordmark } from '@/components/brand/jwa-wordmark'
import { toast } from 'sonner'
import { getSupabaseClient } from '@/lib/auth/client'
import { clientLogger } from '@/lib/client-logger'

const log = clientLogger('AuthPage')

function AuthContent() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  // Handle success and error messages from URL parameters
  const error = searchParams.get('error')
  const errorDescription = searchParams.get('error_description')
  const successMessage = searchParams.get('message')
  
  // Handle URL parameter messages in useEffect to avoid SSR issues
  useEffect(() => {
    // Show success message if present
    if (successMessage && !isLoading) {
      toast.success('Registration Complete!', {
        description: successMessage
      })
      
      // Clear message parameter from URL
      const newUrl = new URL(window.location.href)
      newUrl.searchParams.delete('message')
      router.replace(newUrl.pathname + newUrl.search, { scroll: false })
    }
    
    // Show error message if present
    if (error && !isLoading) {
      let errorMessage = 'Authentication error'
      
      switch (error) {
        case 'server_error':
          errorMessage = 'Server error during authentication. Please try again.'
          break
        case 'exchange_failed':
          errorMessage = 'Email confirmation failed. Please try signing up again.'
          break
        case 'access_denied':
          errorMessage = 'Access denied. Please check your email and try again.'
          break
        default:
          errorMessage = errorDescription || 'An authentication error occurred'
      }
      
      toast.error(errorMessage)
      
      // Clear error parameters from URL
      const newUrl = new URL(window.location.href)
      newUrl.searchParams.delete('error')
      newUrl.searchParams.delete('error_description')
      router.replace(newUrl.pathname + newUrl.search, { scroll: false })
    }
  }, [successMessage, error, errorDescription, router, isLoading])

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !password) {
      toast.error('Please enter both email and password')
      return
    }
    
    try {
      setIsLoading(true)
      
      const supabase = getSupabaseClient()
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      
      if (error) {
        throw error
      }
      
      toast.success('Signed in successfully')
      router.push('/flight')
      router.refresh()
    } catch (error) {
      log.error('Error signing in:', error)
      toast.error((error as Error).message || 'Failed to sign in')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background p-4">
      {/* Ambient background: two soft brand-tinted glows. Token/palette-driven
          so both themes get the treatment; aria-hidden + pointer-events-none
          because they are pure decoration. */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/2 h-[28rem] w-[42rem] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-48 right-[-12%] h-96 w-[30rem] rounded-full bg-sky-400/10 blur-3xl dark:bg-sky-500/10" />
      </div>

      <div className="relative w-full max-w-md">
        <Card className="border-border/60 shadow-lg">
          <CardHeader className="space-y-3 pt-8 pb-2 text-center">
            <h1>
              <JwaWordmark className="text-5xl" />
            </h1>

            <h2 className="text-2xl font-semibold tracking-tight text-foreground">
              Welcome back
            </h2>

            <p className="text-sm text-muted-foreground">
              Sign in to your flight school account
            </p>
          </CardHeader>
        
          <form onSubmit={handleSignIn}>
            <CardContent className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    href="/auth/forgot-password"
                    className="text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="sr-only">
                      {showPassword ? "Hide password" : "Show password"}
                    </span>
                  </Button>
                </div>
              </div>
            </CardContent>

            <CardFooter className="pt-6">
              <Button type="submit" className="h-11 w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  'Sign In with Password'
                )}
              </Button>
            </CardFooter>
          </form>
        </Card>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          JWA Flight School Manager
        </p>
      </div>
    </div>
  )
}

export default function AuthPage() {
  return (
    <Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
      <AuthContent />
    </Suspense>
  )
}
